import { ExtractorDocumentType, postExtractDocument, postExtractDocumentBatch } from '@nemuru-eng/js-api-client';
import React from 'react';

import {
  ExtractionResult,
  FileEntry,
  useDocumentExtractorContext,
} from '@/components/shared/components/DocumentExtractorDialog/contexts/DocumentExtractorContext';
import { rotatePdfAllPagesClockwise90 } from '@/components/shared/components/DocumentExtractorDialog/services/rotatePdfFile';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

export const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.pdf'];
export const MAX_FILE_SIZE_MB = 10;

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const DOCUMENT_TYPE_PRIORITY: ExtractorDocumentType[] = [
  ExtractorDocumentType.DniFront,
  ExtractorDocumentType.NieFront,
  ExtractorDocumentType.NieEuGreenCard,
  ExtractorDocumentType.Payroll,
  ExtractorDocumentType.PersonalIncomeTax,
  ExtractorDocumentType.PensionRevaluation,
  ExtractorDocumentType.BankCertificate,
  ExtractorDocumentType.DniFrontBack,
  ExtractorDocumentType.DniBack,
  ExtractorDocumentType.NieBack,
  ExtractorDocumentType.VehicleInspection,
];

const sortEntries = (entries: FileEntry[]): FileEntry[] => {
  const succeeded = entries.filter((e) => e.status === 'succeeded');
  const pending = entries.filter((e) => e.status === 'pending');
  const failed = entries.filter((e) => e.status === 'failed');
  succeeded.sort((a, b) => {
    const ai = a.result?.documentType != null ? DOCUMENT_TYPE_PRIORITY.indexOf(a.result.documentType) : -1;
    const bi = b.result?.documentType != null ? DOCUMENT_TYPE_PRIORITY.indexOf(b.result.documentType) : -1;
    return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
  });
  return [...succeeded, ...pending, ...failed];
};

const validateFiles = (incoming: File[], t: (key: string) => string): { valid: File[]; errors: string[] } => {
  const valid: File[] = [];
  const errors: string[] = [];
  for (const file of incoming) {
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      errors.push(
        `${file.name}: ${t('DOCUMENT_EXTRACTOR_ERROR_UNSUPPORTED_FORMAT')} ${ACCEPTED_EXTENSIONS.join(', ')}`,
      );
    } else if (file.size > MAX_FILE_SIZE_BYTES) {
      errors.push(`${file.name}: ${t('DOCUMENT_EXTRACTOR_ERROR_FILE_TOO_LARGE')} ${MAX_FILE_SIZE_MB} MB`);
    } else {
      valid.push(file);
    }
  }
  return { valid, errors };
};

const rotateImageFile = (file: File, degrees: number): Promise<File> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const swap = degrees === 90 || degrees === 270;
      const canvas = document.createElement('canvas');
      canvas.width = swap ? img.height : img.width;
      canvas.height = swap ? img.width : img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        resolve(blob ? new File([blob], file.name, { type: file.type }) : file);
      }, file.type);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

const prepareFile = (file: File, rotationDegrees: number): Promise<File> => {
  if (rotationDegrees === 0 || file.type === 'application/pdf') return Promise.resolve(file);
  return rotateImageFile(file, rotationDegrees);
};

export const useDocumentExtractorActions = () => {
  const { entries, setEntries, setFileErrors, setApiError, setIsExtracting, dragState, setDragState } =
    useDocumentExtractorContext();
  const { getTextIdTranslation: t } = useTranslation();

  const addFiles = (incoming: File[]) => {
    const { valid, errors } = validateFiles(incoming, t);
    setEntries((prev) => {
      const existingNames = new Set(prev.map((e) => e.file.name));
      const newEntries = valid
        .filter((f) => !existingNames.has(f.name))
        .map((f) => ({
          file: f,
          status: 'pending' as const,
          viewExpanded: true,
          rotationDegrees: 0,
          rotationDegreesAtExtract: undefined,
          ...(f.type === 'application/pdf' ? { pdfRotationSteps: 0, pdfRotationStepsAtExtract: undefined } : {}),
        }));
      return [...prev, ...newEntries];
    });
    setFileErrors(errors);
  };

  const rotateEntry = (index: number) => {
    setEntries((prev) =>
      prev.map((e, i) => {
        if (i !== index) return e;

        const nextRotationDegrees = (e.rotationDegrees + 90) % 360;
        const baseline = e.rotationDegreesAtExtract;

        let nextStatus = e.status;
        if (e.status === 'succeeded') {
          nextStatus = baseline === undefined || nextRotationDegrees !== baseline ? 'pending' : 'succeeded';
        } else if (e.status === 'failed') {
          nextStatus = 'pending';
        }

        return {
          ...e,
          rotationDegrees: nextRotationDegrees,
          status: nextStatus,
          retryError: undefined,
        };
      }),
    );
  };

  const rotatePdfEntry = async (index: number): Promise<void> => {
    const entry = entries[index];
    if (entry.file.type !== 'application/pdf') return;

    try {
      const rotatedFile = await rotatePdfAllPagesClockwise90(entry.file);
      setEntries((prev) =>
        prev.map((e, i) => {
          if (i !== index) return e;
          const prevSteps = e.pdfRotationSteps ?? 0;
          const nextSteps = (prevSteps + 1) % 4;
          const baseline = e.pdfRotationStepsAtExtract;

          let nextStatus = e.status;
          if (e.status === 'succeeded') {
            nextStatus = baseline === undefined || nextSteps !== baseline ? 'pending' : 'succeeded';
          } else if (e.status === 'failed') {
            nextStatus = 'pending';
          }

          return {
            ...e,
            file: rotatedFile,
            rotationDegrees: 0,
            pdfRotationSteps: nextSteps,
            status: nextStatus,
            retryError: undefined,
          };
        }),
      );
    } catch {
      setEntries((prev) =>
        prev.map((e, i) => (i === index ? { ...e, retryError: t('DOCUMENT_EXTRACTOR_ERROR_PDF_ROTATE_FAILED') } : e)),
      );
    }
  };

  const removeEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleView = (index: number) => {
    setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, viewExpanded: !e.viewExpanded } : e)));
  };

  const handleDocTypeChange = (index: number, docType: ExtractorDocumentType | undefined) => {
    setEntries((prev) =>
      prev.map((e, i) => {
        if (i !== index) return e;
        if (e.status === 'succeeded') {
          return {
            ...e,
            assignedDocumentType: docType,
            status: 'pending',
            result: undefined,
            viewExpanded: false,
            rotationDegreesAtExtract: undefined,
            pdfRotationStepsAtExtract: undefined,
          };
        }
        return { ...e, assignedDocumentType: docType };
      }),
    );
  };

  const handleExtract = async () => {
    const pendingIndices = entries.reduce<number[]>((acc, e, i) => {
      if (e.status === 'pending') acc.push(i);
      return acc;
    }, []);
    if (!pendingIndices.length) return;

    setIsExtracting(true);
    setApiError(null);
    try {
      const pendingFiles = await Promise.all(
        pendingIndices.map((i) => prepareFile(entries[i].file, entries[i].rotationDegrees)),
      );
      const documentTypes = pendingIndices.map((i) => entries[i].assignedDocumentType ?? null);
      const response = await postExtractDocumentBatch({ files: pendingFiles, documentTypes });
      const batchResults = response.results;
      setEntries((prev) =>
        sortEntries(
          prev.map((entry, i) => {
            const posInBatch = pendingIndices.indexOf(i);
            if (posInBatch === -1) return entry;
            const result = batchResults[posInBatch];
            const success = result.success;
            return {
              ...entry,
              status: success ? 'succeeded' : 'failed',
              result,
              rotationDegreesAtExtract: success ? entry.rotationDegrees : undefined,
              ...(entry.file.type === 'application/pdf'
                ? success
                  ? { pdfRotationStepsAtExtract: entry.pdfRotationSteps ?? 0 }
                  : { pdfRotationStepsAtExtract: undefined }
                : {}),
            };
          }),
        ),
      );
    } catch {
      setApiError(t('DOCUMENT_EXTRACTOR_ERROR_EXTRACTION_FAILED'));
    } finally {
      setIsExtracting(false);
    }
  };

  const handleRetry = async (index: number) => {
    const entry = entries[index];
    setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, retryError: undefined, viewExpanded: false } : e)));
    setIsExtracting(true);
    try {
      const file = await prepareFile(entry.file, entry.rotationDegrees);
      const result = await postExtractDocument({
        file,
        ...(entry.assignedDocumentType ? { documentType: entry.assignedDocumentType } : {}),
      });
      setEntries((prev) =>
        sortEntries(
          prev.map((e, i) =>
            i === index
              ? {
                  ...e,
                  status: result.success ? 'succeeded' : 'failed',
                  result: result as ExtractionResult,
                  rotationDegreesAtExtract: result.success ? e.rotationDegrees : undefined,
                  ...(e.file.type === 'application/pdf'
                    ? result.success
                      ? { pdfRotationStepsAtExtract: e.pdfRotationSteps ?? 0 }
                      : { pdfRotationStepsAtExtract: undefined }
                    : {}),
                }
              : e,
          ),
        ),
      );
    } catch {
      setEntries((prev) =>
        prev.map((e, i) => (i === index ? { ...e, retryError: t('DOCUMENT_EXTRACTOR_ERROR_RETRY_FAILED') } : e)),
      );
    } finally {
      setIsExtracting(false);
    }
  };

  const handleRowDragStart = (index: number) => setDragState({ source: index, over: null });

  const handleRowDragEnd = () => setDragState({ source: null, over: null });

  const handleRowDragOver = (e: React.DragEvent<HTMLElement>, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragState((prev) => ({ ...prev, over: index }));
  };

  const handleRowDrop = (e: React.DragEvent<HTMLElement>, targetIndex: number) => {
    e.stopPropagation();
    const { source } = dragState;
    if (source === null || source === targetIndex) {
      setDragState({ source: null, over: null });
      return;
    }
    setEntries((prev) => {
      const next = [...prev];
      const [moved] = next.splice(source, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setDragState({ source: null, over: null });
  };

  return {
    addFiles,
    removeEntry,
    toggleView,
    handleDocTypeChange,
    handleExtract,
    handleRetry,
    rotateEntry,
    rotatePdfEntry,
    handleRowDragStart,
    handleRowDragEnd,
    handleRowDragOver,
    handleRowDrop,
  };
};
